import { useState, useRef, useEffect } from 'react';
import { ChevronDown, Check } from 'lucide-react';
import { spacing, shadows } from '../../constants/designTokens';
import { useTheme } from '../../contexts/ThemeContext';

/**
 * Listbox Component
 *
 * A custom select/dropdown component with keyboard navigation support.
 */
const Listbox = ({ value, onChange, options, label, disabled = false }) => {
  const { colors } = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const containerRef = useRef(null);

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close on escape key
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, []);

  const handleSelect = (option) => {
    onChange(option);
    setIsOpen(false);
  };

  return (
    <div ref={containerRef} style={{ position: 'relative' }}>
      {label && (
        <label style={{
          display: 'block',
          fontSize: '0.875rem',
          fontWeight: 500,
          marginBottom: spacing.sm,
          color: colors.foreground,
          fontFamily: 'Sohne, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
        }}>
          {label}
        </label>
      )}

      {/* Trigger Button */}
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        disabled={disabled}
        style={{
          width: '100%',
          height: '40px',
          padding: '0 16px',
          fontSize: '0.875rem',
          fontWeight: 500,
          fontFamily: 'Sohne, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
          color: colors.foreground,
          background: colors.background,
          border: `1px solid ${isOpen ? colors.foreground : (isHovered && !disabled ? colors.foregroundTertiary : colors.border)}`,
          borderRadius: '12px',
          cursor: disabled ? 'not-allowed' : 'pointer',
          transition: 'all 0.15s ease',
          outline: 'none',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          boxSizing: 'border-box',
          opacity: disabled ? 0.5 : 1,
          boxShadow: isOpen ? `0 0 0 4px ${colors.backgroundOffset}` : '0 1px 2px rgba(0,0,0,0.05)',
        }}
      >
        <span>{value?.name || 'Select...'}</span>
        <ChevronDown
          size={16}
          style={{
            transition: 'transform 0.2s',
            transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
            color: colors.foregroundSecondary
          }}
        />
      </button>

      {/* Options Dropdown */}
      {isOpen && !disabled && (
        <div style={{
          position: 'absolute',
          top: '100%',
          left: 0,
          right: 0,
          marginTop: '4px',
          background: colors.background,
          border: `1px solid ${colors.border}`,
          borderRadius: '12px',
          boxShadow: shadows.md,
          zIndex: 50,
          maxHeight: '240px',
          overflowY: 'auto',
          padding: '4px',
        }}>
          {options.map((option) => {
            const isSelected = value?.id === option.id;
            return (
              <button
                key={option.id}
                type="button"
                onClick={() => handleSelect(option)}
                style={{
                  width: '100%',
                  height: '36px',
                  padding: '0 12px',
                  fontSize: '0.875rem',
                  fontWeight: isSelected ? 500 : 400,
                  fontFamily: 'Sohne, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                  color: colors.foreground,
                  background: isSelected ? colors.backgroundOffset : 'transparent',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  transition: 'background 0.15s ease',
                  outline: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  textAlign: 'left'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = colors.backgroundOffset;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = isSelected ? colors.backgroundOffset : 'transparent';
                }}
              >
                <span>{option.name}</span>
                {isSelected && (
                  <Check size={14} style={{ color: colors.foreground }} />
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Listbox;
